import React, { useState, useEffect, useContext } from 'react';
import { Container, Card, Button, Alert, Table, Badge, Spinner, Form } from 'react-bootstrap';
import dayjs from 'dayjs';
import api from '../../api/axios';
import AuthContext from '../../context/AuthContext';
import SettingsContext from '../../context/SettingsContext';
import SiteFooter from '../../components/SiteFooter';
import { isUserCancel, formatPgError } from '../../utils/pgError';

// 날짜만 표시 헬퍼 (예: "2026-05-28 23:59:59" → "2026. 5. 28.")
const formatDate = (v) => v ? dayjs(v).format('YYYY. M. D.') : '';
// 날짜+시간 (결제 이력용)
const formatPaymentDate = (v) => v ? dayjs(v).format('YYYY. M. D. HH:mm') : '-';

// 포트원(PortOne) V2 — KG이니시스 정기결제(빌링) SDK.
const PORTONE_SDK_SRC = 'https://cdn.portone.io/v2/browser-sdk.js';
const PORTONE_STORE_ID = process.env.REACT_APP_PORTONE_STORE_ID || '';
const PORTONE_CHANNEL_KEY = process.env.REACT_APP_PORTONE_CHANNEL_KEY || '';

// 결제창 노출 범위.
// 심사/테스트 기간에는 REACT_APP_PORTONE_SHOPS 에 든 매장에만 노출했지만, 정식 오픈 후에는
// 전체 매장이 대상이다. env 를 비우면 전체 허용, 값을 넣으면 그 매장만 — 문제가 생겼을 때
// 코드 배포 없이 env 하나로 되돌릴 수 있게 '선택적 허용목록'으로 남겨둔다.
const PORTONE_SHOPS = (process.env.REACT_APP_PORTONE_SHOPS || '')
    .split(',').map((s) => s.trim()).filter(Boolean);

const BillingPage = () => {
    const { user } = useContext(AuthContext);
    const { settings, updateSettings } = useContext(SettingsContext);

    // 연락처 가드 — 실제 번호 없이 카드를 등록하면 이후 결제 실패를 알릴 방법이 없다.
    // 설정 페이지로 보내면 결제 흐름이 끊겨 이탈하므로, 여기서 바로 입력받는다.
    const hasPhone = !!(settings?.phone && String(settings.phone).trim());
    const [phoneInput, setPhoneInput] = useState('');
    const [phoneSaving, setPhoneSaving] = useState(false);
    const [phoneError, setPhoneError] = useState('');
    const [billing, setBilling] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [sdkReady, setSdkReady] = useState(false);

    // 이 매장에 결제창을 노출할지. 포트원 키가 없으면 어차피 결제창을 띄울 수 없고,
    // 허용목록이 비어 있으면 전체 매장에 노출한다(정식 오픈 상태).
    const billingEnabled = !!PORTONE_STORE_ID && !!PORTONE_CHANNEL_KEY
        && (PORTONE_SHOPS.length === 0 || PORTONE_SHOPS.includes(String(user?.shopId)));

    // 포트원 SDK 로드
    useEffect(() => {
        if (!billingEnabled) return;
        if (window.PortOne) { setSdkReady(true); return; }
        const s = document.createElement('script');
        s.src = PORTONE_SDK_SRC;
        s.async = true;
        s.onload = () => setSdkReady(true);
        s.onerror = () => setError('결제 SDK 로드에 실패했습니다.');
        document.body.appendChild(s);
    }, [billingEnabled]);

    // 구독 상태 + 결제 이력 로드
    const fetchBilling = async () => {
        try {
            const [billingRes, historyRes] = await Promise.all([
                api.get('/billing'),
                api.get('/billing/history'),
            ]);
            setBilling(billingRes.data.data);
            setHistory(historyRes.data.data || []);
        } catch (err) {
            setError(err.response?.data?.errors?.[0]?.msg || '구독 정보 조회에 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBilling();
    }, []);

    // 포트원(KG) 빌링키 발급.
    // PC: 팝업 → promise 로 billingKey 수신 → 여기서 바로 서버 저장.
    // 모바일: 리디렉션 → redirectUrl(/account/billing/callback) 에서 저장.
    const handleSavePhone = async () => {
        const v = phoneInput.trim();
        if (!/^01[016789]-?\d{3,4}-?\d{4}$/.test(v)) {
            setPhoneError('올바른 휴대폰 번호 형식이 아닙니다. (예: 010-1234-5678)');
            return;
        }
        setPhoneError('');
        setPhoneSaving(true);
        try {
            await api.put('/shops/settings', { phone: v });
            updateSettings({ phone: v });   // 컨텍스트 즉시 반영 → 알럿 사라지고 등록 버튼 활성화
            setSuccess('휴대폰 번호가 저장되었습니다.');
        } catch (err) {
            setPhoneError(err.response?.data?.errors?.[0]?.msg || '저장에 실패했습니다.');
        } finally {
            setPhoneSaving(false);
        }
    };

    const handleSubscribe = async () => {
        if (!window.PortOne) {
            setError('결제 SDK가 준비되지 않았습니다.');
            return;
        }
        setError('');
        setSubmitting(true);
        try {
            const customerId = `wn_shop_${user?.shopId}`;
            const owner = billing?.owner || {};
            const response = await window.PortOne.requestIssueBillingKey({
                storeId: PORTONE_STORE_ID,
                channelKey: PORTONE_CHANNEL_KEY,
                billingKeyMethod: 'CARD',
                // KG이니시스 모바일 빌링키 발급 필수 항목. '1m' = 월 정기결제 (연 결제는 '1y').
                offerPeriod: { interval: '1m' },
                // 모바일은 대부분 '리디렉션 방식'이라 이 값이 없으면 발급 자체가 에러로 끝난다
                // (PC 모듈 INIStdPay 로 떨어져 "해당기기로는 정상적인 결제가 진행되지 않습니다").
                // PC(팝업)는 아래 promise 로 돌아오고, 모바일은 이 URL 로 쿼리와 함께 복귀한다.
                redirectUrl: `${window.location.origin}/account/billing/callback`,
                issueId: `wn_${user?.shopId}_${Date.now()}`,
                issueName: 'WashNote 월 구독',
                // fullName/phoneNumber/email 은 PC·모바일 모두 필수.
                // phone 은 사전신청(Waitlist) 매장만 보유 — 공개가입 매장은 수집 이력이 없어
                // 플레이스홀더로 나간다. 매장 연락처 컬럼 추가 시 owner.phone 하나만 채우면 된다.
                customer: {
                    customerId,
                    fullName: owner.name || user?.displayName || '사장님',
                    phoneNumber: owner.phone || '010-0000-0000',
                    email: owner.email || 'support@washnote.kr',
                },
            });
            // 리디렉션(모바일) 환경에서는 여기까지 오지 않고 redirectUrl 로 이동한다.
            // 혹시 응답 없이 돌아오면 이동 중이므로 아무것도 하지 않는다.
            if (!response) return;
            // 실패 시 응답에 code/message 가 담김
            if (response?.code != null) {
                // 사용자가 창을 닫은 경우는 조용히 되돌린다(빨간 알럿을 띄우지 않는다)
                if (!isUserCancel(response.code, response.message)) {
                    setError(formatPgError(response.code, response.message, '빌링키 발급에 실패했습니다.'));
                }
                setSubmitting(false);
                return;
            }
            await api.post('/billing/portone/billing-key', {
                billingKey: response.billingKey,
                customerKey: customerId,
            });
            setSuccess('결제 수단이 등록되었습니다.');
            await fetchBilling();
        } catch (err) {
            const msg = err.response?.data?.errors?.[0]?.msg || err?.message || '결제 요청에 실패했습니다.';
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const handleRetry = async () => {
        setError('');
        setSuccess('');
        setSubmitting(true);
        try {
            const res = await api.post('/billing/retry');
            setSuccess(res.data?.message || '결제가 완료되었습니다. 서비스가 정상화되었습니다.');
            await fetchBilling();
        } catch (err) {
            // 백엔드가 정규화한 message 우선 (PG raw 응답 노출 안 함)
            const msg = err.response?.data?.message
                || err.response?.data?.errors?.[0]?.msg
                || '결제에 실패했습니다. 카드를 확인해주세요.';
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    // 해지 예약 철회 — 빌링키가 그대로 있으므로 카드 재입력이 필요 없다.
    const handleResume = async () => {
        setError(''); setSuccess(''); setSubmitting(true);
        try {
            const res = await api.post('/billing/resume');
            setSuccess(res.data?.message || '구독이 계속 유지됩니다.');
            await fetchBilling();
        } catch (err) {
            setError(err.response?.data?.errors?.[0]?.msg || err.response?.data?.message || '처리에 실패했습니다.');
        } finally {
            setSubmitting(false);
        }
    };

    // 종료된 구독을 저장된 카드로 재개 (즉시 결제).
    // 실패하면 서버가 402 + PG 사유를 주므로 그대로 보여주고, 아래 '다른 카드로 등록'으로 유도한다.
    const handleResubscribe = async () => {
        setError(''); setSuccess(''); setSubmitting(true);
        try {
            const res = await api.post('/billing/resubscribe');
            setSuccess(res.data?.message || '구독이 다시 시작되었습니다.');
            await fetchBilling();
        } catch (err) {
            setError(err.response?.data?.message
                || err.response?.data?.errors?.[0]?.msg
                || '결제에 실패했습니다. 다른 카드로 등록해주세요.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = async () => {
        if (!window.confirm('정말 구독을 취소하시겠습니까? 다음 결제일에 종료됩니다.')) return;
        setError('');
        try {
            await api.post('/billing/cancel');
            setSuccess('구독이 취소되었습니다. 다음 결제일에 종료됩니다.');
            await fetchBilling();
        } catch (err) {
            setError(err.response?.data?.errors?.[0]?.msg || '취소에 실패했습니다.');
        }
    };

    if (loading) {
        return (
            <Container className="py-4">
                <Spinner animation="border" />
            </Container>
        );
    }

    const shop = billing?.shop || {};
    const plan = billing?.plan;
    const subscription = billing?.subscription;
    const planAmount = plan?.monthly_amount ?? 33000;
    const planName = plan?.name ?? '베이직';
    const isActive = shop.subscription_status === 'active';
    const isTrialing = shop.subscription_status === 'trialing';
    const isPastDue = shop.subscription_status === 'past_due';
    const isScheduled = subscription?.status === 'scheduled';   // trial 중 카드 등록 완료 (첫 결제 대기)

    const canShowBilling = billingEnabled;

    // 카드 등록/변경 CTA.
    // Subscriptions row 는 매장당 1개(uq_shop_sub)이고 취소해도 남기 때문에,
    // 상태별로 문구만 달라지고 동작(빌링키 재발급 → 서버 upsert)은 모두 동일하다.
    const hasLiveSub = !!subscription && ['active', 'scheduled'].includes(subscription.status);
    const isCancelScheduled = hasLiveSub && !!subscription.canceled_at;   // 해지 예약, 기간 남음
    const isSubCanceled = !!subscription && subscription.status === 'canceled';  // 완전 종료
    // past_due 는 아래 전용 카드(같은 카드 재시도 + 다른 카드 등록)에서 처리한다.
    // 결제 미오픈 매장(canShowBilling=false)은 구독이 살아있으면 카드를 바꿀 수도 없으니
    // '카드 변경'과 '준비 중' 안내가 같이 뜨는 조합을 피하고 카드 자체를 숨긴다.
    const showBillingCard = !isPastDue && (canShowBilling || !subscription);
    // 살아있는 구독이면 카드만 교체되고 청구가 없다 → 결제 사전고지는 신규/재구독일 때만.
    const ctaLabel = !subscription
        ? (isTrialing ? '결제 수단 등록' : '구독 시작')
        : isCancelScheduled ? '구독 계속하기'
        : isSubCanceled ? '다시 구독하기'
        : '카드 변경';
    // 저장된 카드로 바로 처리 가능한 경우 — 카드 재입력을 요구하지 않는다.
    const savedCard = subscription?.card_company
        ? `${subscription.card_company} ${subscription.card_number_masked || ''}`.trim()
        : null;
    const canUseSavedCard = !!subscription?.has_billing_key;
    const ctaHint = isCancelScheduled
        ? '예약된 해지를 취소하고 구독을 계속 유지합니다. 남은 기간과 결제일은 그대로이며 추가 청구는 없습니다.'
        : hasLiveSub
            ? '새 카드를 등록하면 결제 카드만 교체됩니다. 결제일과 금액은 그대로 유지되며 추가 청구는 없습니다.'
            : (isSubCanceled && canUseSavedCard)
                ? `등록된 카드로 바로 다시 구독할 수 있습니다. 진행 시 ${planAmount.toLocaleString()}원이 즉시 결제됩니다.`
                : '버튼을 누르면 카드 등록 창이 열립니다. 등록 완료 후 자동으로 반영됩니다.';

    return (
        <Container className="py-4" style={{ maxWidth: 800 }}>
            <h2>구독 관리</h2>
            <p className="text-muted">월 구독 결제 및 결제 이력을 관리합니다.</p>

            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            {/* 연락처 미등록 — 카드 등록 전에 바로 입력받는다(설정 페이지로 보내면 흐름이 끊겨 이탈). */}
            {canShowBilling && !hasPhone && (
                <Alert variant="warning">
                    <Alert.Heading as="h6" className="mb-2">결제 전 휴대폰 번호가 필요합니다</Alert.Heading>
                    <p className="mb-2 small">
                        결제 실패 시 안내받을 번호입니다. 등록해야 카드를 등록할 수 있습니다.
                    </p>
                    <div className="d-flex gap-2 align-items-start flex-wrap">
                        <Form.Control
                            type="tel"
                            value={phoneInput}
                            onChange={(e) => setPhoneInput(e.target.value)}
                            placeholder="010-1234-5678"
                            disabled={phoneSaving}
                            style={{ maxWidth: 200 }}
                        />
                        <Button variant="warning" onClick={handleSavePhone} disabled={phoneSaving}>
                            {phoneSaving ? '저장 중...' : '저장'}
                        </Button>
                    </div>
                    {phoneError && <div className="text-danger small mt-2">{phoneError}</div>}
                </Alert>
            )}

            {/* 현재 상태 카드 */}
            <Card className="mb-4">
                <Card.Body>
                    <Card.Title>현재 상태</Card.Title>
                    <div className="d-flex align-items-center gap-2 mb-2">
                        {isActive && <Badge bg="success">활성</Badge>}
                        {isTrialing && <Badge bg="warning">무료 체험 중</Badge>}
                        {isPastDue && <Badge bg="danger">결제 실패</Badge>}
                        {shop.is_early_bird && <Badge bg="info">사전 신청자 (가격 동결)</Badge>}
                    </div>
                    {isTrialing && shop.trial_ends_at && (
                        <p>체험 종료일: {formatDate(shop.trial_ends_at)}</p>
                    )}
                    {subscription && (
                        <>
                            <p>플랜 금액: {subscription.plan_amount?.toLocaleString()}원/월</p>
                            <p>{isScheduled ? '첫 결제 예정일' : '다음 결제일'}: {formatDate(subscription.next_billing_date)}</p>
                            {/* 카드 정보는 PG 응답 구조에 따라 비어 올 수 있다. 줄을 통째로
                                숨기면 어떤 카드가 걸려 있는지 확인할 방법이 없으므로 항상 노출한다. */}
                            <p>
                                결제 카드: {subscription.card_company || subscription.card_number_masked
                                    ? `${subscription.card_company || ''} ${subscription.card_number_masked || ''}`.trim()
                                    : <span className="text-muted">정보 없음 (등록은 완료됨)</span>}
                            </p>
                            {isScheduled && !subscription.canceled_at && (
                                <Alert variant="info" className="mt-2">
                                    결제 수단이 등록되었습니다. 무료 체험이 끝나는 <strong>{formatDate(subscription.next_billing_date)}</strong>에 첫 결제가 자동으로 진행됩니다.
                                </Alert>
                            )}
                            {subscription.canceled_at && (
                                <Alert variant="warning" className="mt-2">
                                    {isScheduled ? '결제 예약이 취소되었습니다.' : '구독이 취소되었습니다.'} {formatDate(subscription.next_billing_date)}에 종료됩니다.
                                </Alert>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* 결제위젯 */}
            {showBillingCard && (
                <Card className="mb-4">
                    <Card.Body>
                        <Card.Title>{ctaLabel}</Card.Title>
                        {/* 상품 정보 — 결제 전 사전고지 (전자상거래법). 카드 교체/해지 철회는
                            새로 청구되는 게 없으므로 생략한다. */}
                        {!hasLiveSub && (
                        <div className="mb-3">
                            <p className="mb-1"><strong>{planName} 플랜</strong> — 월 {planAmount.toLocaleString()}원 <span className="text-muted">(부가세 포함)</span></p>
                            <p className="text-muted small mb-1">
                                월 1회 자동 갱신 · 언제든 해지 가능 · 해지 시 다음 결제일부터 청구가 중단됩니다.
                            </p>
                            <p className="text-muted small mb-0">
                                월 단위 선불 결제이며, 중도 해지 시 당월 잔여 기간에 대한 환불은 제공되지 않습니다.
                                자세한 내용은 <a href="https://washnote.kr/terms.html" target="_blank" rel="noopener noreferrer">이용약관</a> 및 <a href="https://washnote.kr/privacy.html" target="_blank" rel="noopener noreferrer">개인정보처리방침</a>을 확인해 주세요.
                            </p>
                        </div>
                        )}
                        {!canShowBilling ? (
                            <Alert variant="info" className="mb-0">
                                현재 결제 시스템을 준비 중입니다. 준비가 완료될 때까지 무료 이용 기간을 연장해 드리고 있으며,
                                {isTrialing && shop.trial_ends_at
                                    ? <> <strong>{formatDate(shop.trial_ends_at)}까지 모든 기능을 무료로 이용</strong>하실 수 있습니다.</>
                                    : ' 그 동안 모든 기능을 무료로 이용하실 수 있습니다.'}
                                {' '}별도 결제는 필요하지 않으며, 결제가 시작되기 전 미리 안내드리겠습니다.
                            </Alert>
                        ) : (
                            <>
                                <p className="text-muted small">{ctaHint}</p>
                                {canUseSavedCard && (isCancelScheduled || isSubCanceled) ? (
                                    <>
                                        {savedCard && (
                                            <p className="mb-2">등록된 카드: <strong>{savedCard}</strong></p>
                                        )}
                                        <div className="d-flex gap-2 flex-wrap">
                                            <Button
                                                variant="primary"
                                                onClick={isCancelScheduled ? handleResume : handleResubscribe}
                                                disabled={submitting}
                                            >
                                                {submitting ? '처리 중...' : ctaLabel}
                                            </Button>
                                            <Button variant="outline-secondary" onClick={handleSubscribe} disabled={!sdkReady || submitting || !hasPhone}>
                                                다른 카드로 등록
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <Button variant="primary" onClick={handleSubscribe} disabled={!sdkReady || submitting || !hasPhone}>
                                        {submitting ? '처리 중...' : ctaLabel}
                                    </Button>
                                )}
                            </>
                        )}
                    </Card.Body>
                </Card>
            )}

            {/* 결제 실패(past_due) — 같은 카드 재시도 + 다른 카드 등록 안내 */}
            {isPastDue && (
                <Card className="mb-4 border-danger">
                    <Card.Body>
                        <Card.Title className="text-danger">결제에 실패했습니다</Card.Title>
                        <p className="mb-3">
                            등록된 카드로 결제가 정상 처리되지 않았습니다. 아래 버튼으로 같은 카드로 다시 시도하거나,
                            다른 카드를 등록할 수 있습니다. <strong>3일 안에 결제가 정상화되지 않으면 서비스 사용이 제한됩니다.</strong>
                        </p>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button variant="danger" onClick={handleRetry} disabled={submitting}>
                                {submitting ? '결제 시도 중...' : '지금 다시 결제하기'}
                            </Button>
                            {canShowBilling && (
                                <Button variant="outline-secondary" onClick={handleSubscribe} disabled={!sdkReady || submitting || !hasPhone}>
                                    다른 카드로 등록
                                </Button>
                            )}
                        </div>
                    </Card.Body>
                </Card>
            )}

            {subscription && !subscription.canceled_at && !isPastDue && (
                <Card className="mb-4">
                    <Card.Body>
                        <Button variant="outline-danger" onClick={handleCancel}>
                            구독 취소
                        </Button>
                        <p className="text-muted small mt-2">
                            취소 시 다음 결제일까지 서비스가 유지됩니다. 일할 환불은 제공되지 않습니다.
                        </p>
                    </Card.Body>
                </Card>
            )}

            {/* 결제 이력 */}
            <Card>
                <Card.Body>
                    <Card.Title>결제 이력</Card.Title>
                    {history.length === 0 ? (
                        <p className="text-muted">결제 이력이 없습니다.</p>
                    ) : (
                        <Table size="sm" hover>
                            <thead>
                                <tr>
                                    <th>날짜</th>
                                    <th>금액</th>
                                    <th>상태</th>
                                    <th>비고</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((p) => (
                                    <tr key={p.id}>
                                        <td style={{ whiteSpace: 'nowrap' }}>{formatPaymentDate(p.paid_at || p.created_at)}</td>
                                        <td>{p.amount?.toLocaleString()}원</td>
                                        <td>
                                            {p.status === 'success' && <Badge bg="success">성공</Badge>}
                                            {p.status === 'failed' && <Badge bg="danger">실패</Badge>}
                                            {p.status === 'refunded' && <Badge bg="secondary">환불</Badge>}
                                        </td>
                                        <td className="text-muted small">{p.failure_reason || ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            <SiteFooter />
        </Container>
    );
};

export default BillingPage;
